import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useCreateReferenceRateValue } from 'src/features/referenceRateValues/api/useCreateReferenceRateValue';
import { LIST_REFERENCE_RATE_VALUES_RESOURCE } from 'src/features/referenceRateValues/api/useListReferenceRateValues';
import { REFERENCE_RATE_RESOURCE } from 'src/features/referenceRates/api/useGetReferenceRate';
import { LIST_REFERENCE_RATES_RESOURCE } from 'src/features/referenceRates/api/useListReferenceRates';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import ReferenceRateValueFormModal from 'src/features/referenceRateValues/components/forms/ReferenceRateValueFormModal';

interface CreateReferenceRateValueFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateReferenceRateValueFormModal<TData>({ className }: CreateReferenceRateValueFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { referenceRateId = '' } = useParams();

    const { mutate, isPending } = useCreateReferenceRateValue({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATE_VALUES_RESOURCE] });
            // A new value can close the previous one and change which value covers today.
            void queryClient.invalidateQueries({ queryKey: [REFERENCE_RATE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATES_RESOURCE] });
            toast({
                title: formatMessage(referenceRateValuesMessages.createReferenceRateValueSuccess),
                description: formatMessage(referenceRateValuesMessages.createReferenceRateValueSuccessDescription),
            });
        },
    });

    return (
        <ReferenceRateValueFormModal
            className={className}
            mutate={(values, onSuccess, onError) => mutate({ ...values, referenceRateId }, { onSuccess, onError })}
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(referenceRateValuesMessages.value).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(referenceRateValuesMessages.addReferenceRateValueSubtitle)}
        />
    );
}

export default CreateReferenceRateValueFormModal;
